import { computed, h, defineComponent, SetupContext } from 'vue';
// @ts-ignore
import { BDropdown, BDropdownItem } from 'buefy/dist/esm/dropdown';
import { Button } from '@/ui/components/Button';
import { t } from '@/ui/i18n/i18n';
import { SelectOptions, SetterCallback } from '@/ui/types';

export interface DropdownProps<T = number> {
  options: SelectOptions<T>;
  value: T | null;
  disabled: boolean;
  placeholder?: string | null;
  position?: string;
  activable?: boolean;
  icon?: string;
  arrowless?: boolean;
  scrollable?: boolean;
  maxHeight?: number;
  isGrayText?: boolean;
  appendToBody?: boolean;
}

export const Dropdown = defineComponent<DropdownProps>({
  name: 'Dropdown',
  props: [
    'options', 'value', 'disabled', 'placeholder',
    'position', 'activable', 'icon', 'arrowless',
    'scrollable', 'maxHeight', 'isGrayText', 'appendToBody',
  ],
  setup(props, context: SetupContext) {
    const selected = computed(
      () => {
        const s = props.options.find(item => item.value === props.value);
        if (s) {
          return s.label;
        }
        if (props.placeholder === undefined) {
          return t('form.select-item');
        }
        if (props.placeholder === null) {
          return null;
        }
        return props.placeholder;
      },
    );

    const position = props.position || 'is-bottom-right';
    const activable = props.activable === undefined ? true : props.activable;

    const angleIcon = (active: boolean) => (active ? 'angle-up' : 'angle-down');
    const hasItemSelected = (value: unknown) => {
      return value !== null;
    };

    return function render() {
      return h(
        BDropdown,
        {
          props: {
            scrollable: props.scrollable,
            maxHeight: props.maxHeight,
            appendToBody: props.appendToBody ?? false,
          },
          class: {
            'invisible-is-active': !activable,
            'has-item-selected': hasItemSelected(props.value),
          },
          attrs: {
            position,
            disabled: props.disabled,
          },
          scopedSlots: {
            trigger: ({ active }: { active: boolean }) => h(
              Button,
              {
                props: {
                  type: 'is-text',
                  btnClass: `has-text-weight-normal is-dropdown ${props.isGrayText ? 'is-dropdown__grey' : ''}`,
                },
                attrs: {
                  iconRight: props.arrowless === true ? undefined : angleIcon(active),
                  iconLeft: props.icon,
                },
              },
              selected.value,
            ),
          },
          on: { input: ((value: unknown) => context.emit('input', value)) },
        },
        props.options.map(
          item => h(BDropdownItem, { props: { value: item.value, disabled: item.disabled } }, item.label),
        ),
      );
    };
  },
});

export function dropdown<T>(props: DropdownProps<T>, input: SetterCallback<T>) {
  return h(Dropdown, {
    props,
    on: { input },
  });
}
